import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (credential: string, onSuccess?: () => void) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  handleTokenExpiration: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      // Optimistic bootstrap: use cached snapshot to avoid blocking UI
      try {
        const cached = localStorage.getItem('authUserSnapshot');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === 'object') {
            setUser(parsed);
            // We still mark loading true briefly until we verify
            setIsLoading(true);
          }
        }
      } catch {}
      loadUser();
    } else {
      // Try silent refresh via httpOnly refresh cookie to rehydrate session without user interaction
      (async () => {
        try {
          const resp = await apiService.auth.refresh({ timeout: 2500 });
          if (resp.data?.success && (resp.data as any).data?.token) {
            try { localStorage.setItem('authToken', (resp.data as any).data.token); } catch {}
            await loadUser();
            return;
          }
        } catch {}
        setIsLoading(false);
      })();
    }

    // Listen for token expiration events
    const handleTokenExpired = () => {
      handleTokenExpiration();
    };

    window.addEventListener('tokenExpired', handleTokenExpired);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, []);

  const loadUser = async () => {
    try {
      // Use a short timeout for the initial profile check to avoid long hangs
      const response = await apiService.auth.getProfile({ timeout: 2500 });
      
      if (response.data.success) {
        const userData = response.data.data;
        setUser({
          ...userData,
          _id: userData._id || userData.id,
        });
        try { localStorage.setItem('authUserSnapshot', JSON.stringify({
          ...userData,
          _id: userData._id || userData.id,
        })); } catch {}
      } else {
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        // Token expired or invalid - clear it and redirect to login
        handleTokenExpiration();
        return;
      }
      // Network/timeouts or other errors: try a one-shot silent refresh before giving up
      try {
        const resp = await apiService.auth.refresh({ timeout: 2000 });
        if (resp.data?.success && (resp.data as any).data?.token) {
          try { localStorage.setItem('authToken', (resp.data as any).data.token); } catch {}
          // Retry loading profile quickly (no long spinner)
          const retry = await apiService.auth.getProfile({ timeout: 2000 });
          if (retry.data?.success) {
            const userData = retry.data.data;
            setUser({ ...userData, _id: userData._id || userData.id });
            try { localStorage.setItem('authUserSnapshot', JSON.stringify({ ...userData, _id: userData._id || userData.id })); } catch {}
            return;
          }
        }
      } catch (_) {
        // ignore and fall back to cached snapshot if any
      }
      // Do not clear token on transient errors; keep cached user if present
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credential: string, onSuccess?: () => void) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.auth.googleLogin(credential);
      
      if (response.data.success) {
        const { token, user: userData } = response.data.data;
        try { localStorage.setItem('authToken', token); } catch {}
        setUser({
          ...userData,
          _id: userData._id || userData.id,
        });
        
        // Call the success callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.data.error?.message || 'Login failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const handleTokenExpiration = () => {
    setUser(null);
    setIsLoading(false);
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    user,
    isLoading,
    error,
    login,
    logout,
    clearError,
    handleTokenExpiration,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 